// API contract (ADR-0005). Hand-written types shared by api/ and web/. Grows with each endpoint.

import type { DateFormat, EnumField, NumberFormat } from "./normalize";
import type { SourceType } from "./rows";
import type { BoxName } from "./sources";

export const ROLES = ["super_admin", "admin", "operator", "viewer"] as const;
export type Role = (typeof ROLES)[number];

export type ErrorCode =
  | "INVALID_REQUEST" | "UNAUTHENTICATED" | "LOGIN_FAILED" | "FORBIDDEN" | "MUST_SET_PASSWORD" | "CSRF_REJECTED"
  | "NOT_FOUND" | "FILE_ALREADY_UPLOADED" | "FILE_TOO_LARGE" | "ROW_ERRORS"
  | "REQUIRED_FIELDS_MISSING" | "AMBIGUOUS_FORMAT" | "VALUE_MAP_MISSING" | "JUSTIFICATION_REQUIRED" | "EXPORT_TOO_LARGE"
  | "RATE_LIMITED" | "VERSION_CONFLICT" | "RECORD_EXISTS" | "NO_CHANGE" | "INTERNAL_ERROR";

/** Every 4xx/5xx body. Code-specific extra fields ride along. */
export type ErrorBody = { code: ErrorCode; message: string; requestId: string } & Record<string, unknown>;

export type FieldError = { field: string; message: string };

/** POST /api/session */
export type LoginRequest = { username: string; password: string };
/** POST /api/session (200) and GET /api/session */
export type SessionResponse = { username: string; role: Role; mustSetPassword: boolean };
/** POST /api/session/password (204) */
export type SetPasswordRequest = { newPassword: string; newPasswordConfirm: string };
/** POST /api/session/password (204) on a normal session: `currentPassword` is required (ADR-0017). */
export type ChangePasswordRequest = SetPasswordRequest & { currentPassword: string };
/** GET /api/account. `previousLoginAt`: the last login_succeeded before the one that opened this session; null if none. */
export type AccountResponse = { username: string; role: Role; createdAt: string; previousLoginAt: string | null };

// ---- E2 / E2b: sources, profiles, upload (spec §1-§3, §5; ADR-0003, ADR-0004, ADR-0005) ----

/** One item of 422 ROW_ERRORS `rowErrors` (at most 20). A repeated record key is one item: reason `DUPLICATE_KEY`, `duplicateRows` lists every row. */
export interface RowError { rowNo: number; column: string; value: string; reason: string; duplicateRows?: number[] }
/** 422 ROW_ERRORS extra fields. */
export type RowErrorsExtra = { rowErrors: RowError[]; totalErrors: number };
/** 422 AMBIGUOUS_FORMAT extra field: `117.000` -> options tr / en / plain. */
export interface FormatQuestion { field: string; column: string; sample: string; options: NumberFormat[] }
/** 422 VALUE_MAP_MISSING extra field. */
export interface UnknownValues { field: string; column: string; values: string[] }

/** `{ [field]: column }`: system field code -> header text in the file. */
export type FieldMapping = Record<string, string>;
/** `{ movementType: { "Mal Kabul": "goods_receipt" } }` (ADR-0005). */
export type ValueMap = Partial<Record<EnumField, Record<string, string>>>;
/** Text number / text date formats (spec §3). Numeric and date cells need none. */
export type ProfileFormat = { numberFormat?: NumberFormat; dateFormat?: DateFormat };

/** The `mapping` JSON of the multipart upload and preview requests. */
export type UploadMapping = {
  /** Optional when `profileId` is given. */
  sourceType?: SourceType;
  /** A saved profile supplies `mapping`, `format` and `valueMap` unless the request carries its own. */
  profileId?: number;
  mapping?: FieldMapping;
  format?: ProfileFormat;
  valueMap?: ValueMap;
};

/** POST /api/profiles. Profiles are never updated: every save is a new profile (spec §2.6). */
export type SaveProfileRequest = {
  name: string;
  sourceType: SourceType;
  /** Header set of the file, any order. Stored sorted; exactly the same set selects the profile automatically. */
  headers: string[];
  mapping: FieldMapping;
  format?: ProfileFormat;
  valueMap?: ValueMap;
};
/** POST /api/profiles (201) */
export type SaveProfileResponse = { profileId: number };
/** GET /api/profiles?sourceType= */
export type ProfileResponse = {
  profileId: number;
  name: string;
  sourceType: SourceType;
  headers: string[];
  mapping: FieldMapping;
  format: ProfileFormat;
  valueMap: ValueMap;
  createdAt: string;
  createdBy: string;
};

/** `rowCount`: non-empty rows below the header row. */
export type SheetInfo = { name: string; rowCount: number; columnCount: number };
/** First filled value of the column. `raw`: number or text as in the cell (a date or time cell as `YYYY-MM-DD` / `SS:DD`). `normalized` is null when it does not parse; `problem` then names the reason. */
export type Sample = { field: string; column: string; raw: string | number | null; normalized: string | number | null; problem?: string };
export type Suggestion = { field: string; column: string | null; required: boolean };

/** POST /api/uploads/preview. Stores nothing. */
export type PreviewResponse = {
  sheets: SheetInfo[];
  /** The sheet that was read. */
  sheet: string;
  headers: string[];
  /** Saved profile whose header set equals the file's exactly (spec §2.2). */
  matchedProfile: ProfileResponse | null;
  /** Source type in use: the request's, else the matched profile's, else null (the user is asked). */
  sourceType: SourceType | null;
  /** True when `sourceType` is the system's guess from the headers (no request choice, no matched profile), not a choice. */
  sourceTypeGuessed: boolean;
  /** Name-based suggestion for every field of the type (empty list while `sourceType` is null). */
  suggestions: Suggestion[];
  /** Mapping and format in effect: the request's, else the profile's, else the suggestion. Samples and `required` use these. */
  mapping: FieldMapping;
  format: ProfileFormat;
  valueMap: ValueMap;
  samples: Sample[];
  /** ADR-0018: dated types, when the request has no `period` and the file is valid: whole months the event dates cover. Else null. */
  suggestedPeriod: { start: string; end: string } | null;
  /** Null while `sourceType` is null. */
  required: { matched: number; total: number } | null;
  questions: FormatQuestion[];
  unknownValues: UnknownValues[];
  /** Null while the file cannot be read as a valid version (no source type, missing fields, row errors, questions). */
  changes: ChangeSummary | null;
};

/** What the new file does to the source type's previous version (spec §5). Preview says it before the upload. */
export type ChangeSummary = {
  /** Null for the first version of the source type. */
  previousUploadId: number | null;
  newCount: number;
  changedCount: number;
  removedCount: number;
  /** changedCount + removedCount > 0: the upload needs a `justification` (Ek-2 2.2 f). */
  justificationRequired: boolean;
  /** ADR-0018: previous-version records outside the period that the new version carries over. 0 without a period. */
  carriedCount: number;
};
/** 422 JUSTIFICATION_REQUIRED extra fields. */
export type JustificationRequiredExtra = { changedCount: number; removedCount: number };

/** POST /api/uploads (201) */
export type UploadResponse = { uploadId: number; rowCount: number; newCount: number; changedCount: number; removedCount: number };
/** 409 FILE_ALREADY_UPLOADED extra field: "Bu dosya #N ile zaten yüklü". */
export type FileAlreadyUploadedExtra = { uploadId: number };

export type UploadOrigin = "file" | "manual";
/** POST /api/manual-entries (201): the new version plus the key of the added or corrected record. */
export type ManualEntryResponse = UploadResponse & { recordKey: string };
/** POST /api/manual-entries body (ADR-0016). `fields` use plain formats: number as JSON number, date YYYY-MM-DD, time HH:MM. */
export type ManualEntryRequest = {
  sourceType: SourceType;
  operation: "create" | "correct";
  /** `correct` only; same format as GET /api/records/history. */
  recordKey?: string;
  fields: Record<string, FieldValue>;
  /** The latest version the form opened on; null when the type has no version (create only). */
  basedOnUploadId: number | null;
  justification: string;
};

/** GET /api/uploads?sourceType= (newest first) */
export type UploadItem = {
  uploadId: number;
  createdAt: string;
  uploadedBy: string;
  /** ADR-0016: 'manual' is a manual entry; `fileName` and `sheet` are then null. */
  origin: UploadOrigin;
  fileName: string | null;
  sheet: string | null;
  sourceType: SourceType;
  profileId: number | null;
  profileName: string | null;
  rowCount: number;
  /** ADR-0018: `YYYY-MM-DD`, both null = full state. */
  periodStart: string | null;
  periodEnd: string | null;
  /** Full SHA-256 hex; the screen shows the first 8 characters. */
  fileHash: string;
  justification: string | null;
};
/** GET /api/uploads/:id: the record, its profile and every version of that source type (newest first). */
/** `versionRecordCount`: count(records) of this version (ADR-0018); `rowCount` is the file's row count. */
export type UploadDetailResponse = UploadItem & { profile: ProfileResponse | null; versions: UploadItem[]; versionRecordCount: number };

export type SourceFile = { uploadId: number; file: string; origin: UploadOrigin; uploadedBy: string; sourceType: SourceType; uploadedAt: string; recordCount: number };
export type SourceBox = { sourceTypes: SourceType[]; files: SourceFile[] };
/** E2 box header (spec §6 E2): only the open R1 / R1x / R6 findings warn a box; R5 and DATA_* do not. */
export type BoxWarning = { rule: "R1" | "R1x" | "R6"; /** the finding's `subject`: declaration no, movement no or camera codes */ record: string };
export type BoxStatus = {
  status: "current" | "warning";
  /** "3 gün eksik kayıt" ...; the screen writes the "⚠". Absent while `status` is current. */
  warning?: string;
  warnings: BoxWarning[];
  /** Days without camera recording (R6), each day counted once. Absent when no R6 finding warns the box. */
  missingDays?: number;
};
/** GET /api/sources: the latest version of each loaded source type; box `status` comes from the open findings (#16). */
export type SourcesResponse = { boxes: Record<BoxName, SourceBox & BoxStatus>; totalRecords: number };

// ---- E6: data history, integrity (spec §5, §6 E6; ADR-0004) ----

export type FieldValue = string | number | null;
export type FieldDiff = { field: string; old: FieldValue; new: FieldValue };
/** GET /api/records/history?sourceType=&recordKey=: one item per version of the source type, from the record's first appearance on. */
export type RecordVersion = {
  uploadId: number;
  createdAt: string;
  uploadedBy: string;
  origin: UploadOrigin;
  fileName: string | null;
  fileHash: string;
  justification: string | null;
  /** False: the version has no row with this key (DATA_REMOVED). `fields` and `rowNo` are then null. */
  present: boolean;
  /** null for a manual-entry version (the copied Excel row no is not exposed). */
  rowNo: number | null;
  manualEntryId: number | null;
  fields: Record<string, FieldValue> | null;
  /** Against the previous version. Empty for the first one, for an absent one and for the first one after a gap. */
  changes: FieldDiff[];
};
export type RecordHistoryResponse = { sourceType: SourceType; recordKey: string; versions: RecordVersion[] };

/** GET /api/uploads/:id/records?keyContains&page&pageSize: the rows of one upload, by Excel row no (spec §6 E2 Kayıtlar). */
/** `sourceUploadId` (ADR-0018): the file upload that holds this row; null in a manual-entry version for a record a manual entry added or corrected (row "—"). */
export type UploadRecordItem = { rowNo: number | null; sourceUploadId: number | null; manualEntryId: number | null; recordKey: string; fields: Record<string, FieldValue> };
export type UploadRecordsResponse = { items: UploadRecordItem[]; total: number; page: number; pageSize: number };

/** One DATA_CHANGED / DATA_REMOVED finding of an upload. */
export type DataChangeItem = {
  findingId: number;
  rule: "DATA_CHANGED" | "DATA_REMOVED";
  severity: "info" | "warning";
  recordKey: string;
  /** DATA_CHANGED: old -> new per field. DATA_REMOVED: empty. */
  fields: FieldDiff[];
  status: "open" | "closed";
  closedByUploadId: number | null;
  /** "Kapandı — sonraki yükleme #N" / "Kapandı — sonraki elle giriş #N" (shared/findings.ts closeReasonText). Null while open. */
  closeText: string | null;
};
/** GET /api/uploads/:id/changes */
export type UploadChangesResponse = { uploadId: number; origin: UploadOrigin; sourceType: SourceType; changedCount: number; removedCount: number; items: DataChangeItem[] };

/** GET /api/integrity. Counts cover all stored files, not only the latest versions. A failure: the stored file differs or cannot be read. */
export type IntegrityResponse = {
  /** Stored Excel files only. */
  fileCount: number;
  manualEntryCount: number;
  recordCount: number;
  failures: { uploadId: number; origin: UploadOrigin; file: string; result: "modified" | "missing" }[];
  recordedChangeCount: number;
};

// ---- E3: reconciliation, findings (spec §4, §6 E3; ADR-0003, ADR-0005, ADR-0006) ----
// No penalty or TL-estimate field exists (CLAUDE.md). Money appears only as facts: quantity, valueUsd, importedInputEquivalent;
// R7's amountTry / impliedRate are accounting facts.

export type Severity = "critical" | "warning" | "info";
export const FINDING_RULES = ["R1", "R1x", "R2", "R3", "R4", "R5", "R6", "R7", "DATA_CHANGED", "DATA_REMOVED"] as const;
export type FindingRule = (typeof FINDING_RULES)[number];
/** The rules a run produces (DATA_* come from uploads, #14). */
export type RuleCode = Exclude<FindingRule, "DATA_CHANGED" | "DATA_REMOVED">;
export type FindingKind =
  | "quantity_mismatch" | "no_erp_receipt" | "no_hs_mapping" | "unreferenced_receipt" | "unreferenced_shipment" | "negative_balance" | "bom_deviation"
  | "negative_product_stock" | "forecast_shortfall" | "over_fulfilment" | "camera_outage" | "no_accounting_entry";

/** A declaration item (R1 / R1x) in the E3 top line. Quantities in the declaration unit (kg). */
export type RunItem = { rule: "R1" | "R1x"; declaration: string; itemNo: string; declaredKg: number; /** group (declaration + GTİP) values sit on the first item of the group; null on the others, so column sums count a group once */ erpKg: number | null; diffKg: number | null; matched: boolean };
/** R7: where a declaration has accounting lines, TL amount ÷ value = implied rate (a fact, not a finding). */
export type ImpliedRate = { declaration: string; vouchers: string[]; amountTry: number; impliedRate: number | null };
/** E3 top line (spec §4): the system counts everything. */
export type RunSummary = {
  importItems: number;
  exportItems: number;
  stockMovements: number;
  durationMs: number;
  matchedCount: number;
  unmatchedCount: number;
  totalDiffKg: number;
  items: RunItem[];
  impliedRates: ImpliedRate[];
  /** A rule runs only when all its input source types are loaded; otherwise it is listed here and its open findings stay open. */
  skippedRules: { rule: RuleCode; missing: SourceType[] }[];
};
/** A setting that changed between two settings versions (ADR-0006). */
export type SettingChange = { key: string; old: unknown; new: unknown };
/** `close_reason` jsonb (glossary §4). Run findings: `uploadIds` and/or `settings`, else `appVersions`, else `runDates`. DATA_*: `nextUploadId`. */
export type CloseReason = {
  nextUploadId?: number;
  uploadIds?: number[];
  settings?: SettingChange[];
  appVersions?: [string, string];
  runDates?: [string, string];
};

/** POST /api/runs (201) and GET /api/runs/latest (404 NOT_FOUND until the first run). */
export type RunResponse = {
  runId: number;
  startedAt: string;
  runBy: string;
  settingsVersion: number;
  appVersion: string;
  summary: RunSummary;
  /** The versions the run read, one per loaded source type. */
  uploads: { sourceType: SourceType; uploadId: number; fileName: string }[];
  openedCount: number;
  closedCount: number;
  /** A run holds the run lock right now (GET /api/runs/latest; a further POST /api/runs would wait). Only in GET /api/runs/latest. */
  running?: boolean;
};

export type FindingStatus = "open" | "closed";
export type FindingItem = {
  findingId: number;
  rule: FindingRule;
  severity: Severity;
  /** Null for DATA_*. */
  kind: FindingKind | null;
  title: string;
  status: FindingStatus;
  openedAt: string;
  closedAt: string | null;
  /** "Kapandı — veri düzeltmesiyle, yükleme #12 `03b_…xlsx`, gerekçe: '…'" (shared/findings.ts closeReasonText). Null while open. */
  closeText: string | null;
};
/**
 * GET /api/findings?status=&severity=&rule=&q=&sort=&page=&pageSize=
 * `severity` and `rule` repeat (`severity=critical&severity=warning`) or are comma-separated; `q` searches key and title (case-insensitive);
 * `sort=severity` (default): critical first, then warning, info, then findingId; `sort=openedAt`: newest first.
 */
export type FindingsSort = "severity" | "openedAt";
/** GET /api/runs/latest/items?q=&page=&pageSize= (q: declaration contains; order: as in RunSummary.items) */
export type RunItemsResponse = { items: RunItem[]; total: number; page: number; pageSize: number };
export type FindingsResponse = { items: FindingItem[]; total: number; page: number; pageSize: number };

/** A record the finding is about: source file + row. `rowNo` is null for DATA_* (the row of the upload is on E6). */
export type FindingRecord = {
  sourceType: SourceType; uploadId: number;
  /** Display label from `uploadLabel` ("Elle giriş #N" for a manual entry), never null. */
  fileName: string;
  /** Of the version the run read. */
  origin: UploadOrigin;
  /** `null` when that version is a manual entry (and for DATA_*). */
  rowNo: number | null;
  /** ADR-0018: the upload that holds `rowNo`'s file (a carried record: an older one than `uploadId`; `fileName` is its label). Null for DATA_REMOVED and for a manual record. */
  sourceUploadId: number | null;
  /** ADR-0016 Ek: the manual entry that set this record's value; the "Elle girildi" mark shows only when set. `null` for DATA_REMOVED. */
  manualEntryId: number | null;
  recordKey: string;
};
export type NoteItem = { noteId: number; author: string; body: string; createdAt: string };
/** Money as facts only. */
export type FindingFacts = {
  quantity?: number;
  unit?: string;
  valueUsd?: number;
  /** R5: shortfall × (1 + waste%) and its value at the D1 average unit value. */
  importedInputEquivalent?: { importedInputKg: number; importedInputValueUsd: number };
  /** Rule specific named values: declaredKg, erpKg, diffKg, days ... */
  values: Record<string, string | number | string[] | null>;
};
/** GET /api/findings/:id (right panel of E3) */
export type FindingDetailResponse = FindingItem & FindingFacts & {
  /** Declaration no, movement no, material, camera codes ... */
  subject: string;
  records: FindingRecord[];
  /** Calculation steps, ready to show. */
  steps: string[];
  /** Fixed sentences of spec §4 (R1 risk, R5 tolerance, R6 basis). */
  text: string[];
  /** DATA_CHANGED: old → new per field. */
  fields: FieldDiff[];
  closeReason: CloseReason | null;
  openedByRunId: number | null;
  openedByUploadId: number | null;
  lastTriggeredRunId: number | null;
  closedByRunId: number | null;
  closedByUploadId: number | null;
  notes: NoteItem[];
};
/** POST /api/findings/:id/notes. The author and time come from the session. */
export type AddNoteRequest = { body: string };
/** POST /api/findings/:id/notes (201) */
export type AddNoteResponse = NoteItem;

// ---- E4 İzleme Zinciri (spec §6 E4): GET /api/trace?ref= ----
/** What `ref` was resolved to. A declaration no is a declaration; a movement no is any ERP movement. */
export type TraceRefKind = "import_declaration" | "export_declaration" | "movement" | "work_order";
export type TraceStatus = { completeKg: number; brokenKg: number };
/** One import declaration followed forward: declaration → receipt → work orders → production → exports. Every box names its source file. */
export type TraceChain = {
  declaration: { declarationNo: string; date: string; originCountry: string | null; hsCode: string; quantityKg: number; ipaNo: string | null; file: string; manualEntryId: number | null };
  /** `missingKg`: declared − found in the ERP (0 when none is missing) */
  warehouseReceipt: { movements: { movementNo: string; date: string; warehouse: string | null; quantityKg: number }[]; quantityKg: number; missingKg: number; file: string; hasManualEntry: boolean };
  workOrders: { count: number; firstDate: string | null; lastDate: string | null; allocatedKg: number; list: { workOrder: string; date: string; allocatedKg: number }[]; file: string; hasManualEntry: boolean };
  /** null when no recipe row names the receipt's material. `wastePct` is the norm waste entered in the recipe. */
  production: { material: string; wastePct: number; productKg: number; files: string[]; hasManualEntry: boolean } | null;
  exports: { declarationNo: string; date: string; country: string | null; allocatedKg: number; file: string; manualEntryId: number | null }[];
  traceStatus: TraceStatus;
};
/** One export declaration followed backward to the receipts (same FIFO allocation as the forward chains). */
export type TraceReverse = {
  declaration: { declarationNo: string; date: string; country: string | null; quantityKg: number; file: string; manualEntryId: number | null };
  sources: { declarationNo: string | null; movementNo: string; allocatedKg: number; file: string; manualEntryId: number | null }[];
  traceStatus: TraceStatus;
};
export type TraceResponse = { ref: string; refKind: TraceRefKind; chains: TraceChain[]; reverse: TraceReverse[] };

// E5 DİİB panel (spec §4 R5, §6 E5; docs/design/api-uc-noktalari.md). Dates are ISO `YYYY-MM-DD`. No TL field.
/** GET /api/ipa (document picker). Empty until a DİİB file is loaded. */
export type IpaListResponse = { items: { ipaNo: string; endDate: string; daysLeft: number }[] };
/** A declaration item counted into a DİİB line. */
export type IpaDeclaration = { declarationNo: string; itemNo: string | null; registrationDate: string; quantityKg: number };
/** One commitment line (imports or exports). Forecast terms: exports with a remainder. Input equivalent: forecast shortfall and a recipe. */
export type IpaLineItem = {
  ipaNo: string;
  commitmentType: "imports" | "exports";
  hsCode: string;
  commitmentKg: number;
  fulfilledKg: number;
  fulfilmentPct: number | null;
  remainingKg: number;
  endDate: string;
  daysLeft: number;
  declarations: IpaDeclaration[];
  /** From the DİİB start month to min(today, end date), one item per month (0 when none); last cumulativeKg = fulfilledKg. */
  months: { month: string; kg: number; cumulativeKg: number }[];
  forecastWindowDays?: number;
  lastNDaysKg?: number;
  forecastKg?: number;
  forecastShortfallKg?: number;
  /** Ready-made formula sentence: "Son 90 gün: 36.000 kg → kalan 107 günde ≈ 42.800 kg → öngörülen açık 17.200 kg (varsayım: hız değişmez)". */
  forecastText?: string;
  importedInputKg?: number;
  importedUnitValueUsd?: number;
  importedInputValueUsd?: number;
};
/** On-hand stock against the remaining export commitment (R2 / R4 balances). */
export type IpaStockCoverage = {
  rawMaterialStockKg: number;
  productStockKg: number;
  /** product stock + raw material stock ÷ (recipe quantity × (1 + waste%)) */
  productEquivalentKg: number;
  remainingCommitmentKg: number;
  covers: boolean;
};
/** GET /api/ipa/detail?ipaNo= */
export type IpaDetailResponse = {
  ipaNo: string;
  imports: IpaLineItem | null;
  exports: IpaLineItem | null;
  /** null when there is no export line with a remainder and a recipe */
  stockCoverage: IpaStockCoverage | null;
  /** Fixed statute sentence under the end date line (spec §6 E5). */
  endDateText: string;
};

// ---- E1 Durum Panosu (spec §6 E1): GET /api/dashboard ----
/** Four cards, all computed. No "denetime hazırlık" card. Cards that need a run (`openFindingCount`, `traceabilityPct`) are empty before the first run. */
export type DashboardResponse = {
  /** Open findings by severity (all zero before the first run). */
  openFindingCount: Record<Severity, number>;
  /** All open findings (= the sum of `openFindingCount` = `total` of GET /api/findings?status=open), not cut at 20; 0 before the first run. */
  openFindingTotal: number;
  /** Matched import kg ÷ declared import kg × 100, from R1 of the latest run; null before a run or when nothing is declared. */
  traceabilityPct: number | null;
  /** Σ over R1 comparison units (declaration, GTİP) of min(ERP kg, declared kg); null before a run. */
  matchedKg: number | null;
  declaredKg: number | null;
  /** Days to the nearest DİİB end date (all lines, today = server clock in Europe/Istanbul); negative when it has passed; null without a DİİB file. */
  ipaDaysLeft: number | null;
  sourceStatus: {
    loadedSourceTypes: number;
    totalSourceTypes: number;
    /** Source types that are an input of at least one open finding (any severity; DATA_* → its own source type), in source type order. */
    warnedSourceTypes: SourceType[];
  };
  /** Open findings, critical first then oldest first, at most 20. The full list is GET /api/findings?status=open. */
  openFindings: FindingItem[];
  /** The last 5 uploads, newest first. */
  latestUploads: UploadItem[];
};

// ---- E7 Denetim Raporu (spec §6 E7, ADR-0007): POST /api/reports, GET /api/reports ----
/** Spec §6 E7 section 2 (decision: DİİB materials, the first recipe row). Quantities in kg. `links`: true = ✓. Null where the recipe or a source is missing. */
export type ReconciliationSummary = {
  receiptKg: number;
  consumptionKg: number;
  rawMaterialStockKg: number;
  /** consumption ÷ (1 + waste%) */
  consumptionNetOfWasteKg: number;
  productionKg: number;
  exportKg: number;
  productStockKg: number;
  declaredImportKg: number;
  erpReceiptKg: number;
  declaredErpDiffKg: number;
  links: { rawMaterial: boolean; production: boolean; product: boolean; declaredErp: boolean };
};
/** POST /api/reports (201). The report number is `reports.id`, increasing. */
export type CreateReportResponse = { reportId: number };
/** GET /api/reports?page&pageSize (newest first; default pageSize 50, max 200). */
export type ReportItem = { reportId: number; createdAt: string; createdBy: string };
export type ReportsResponse = { items: ReportItem[]; total: number; page: number; pageSize: number };

/** GET /api/health (session-exempt). `company` is the settings file company name, null when unreadable. */
export type HealthResponse = { status: "ok" | "error"; checks: { database: "ok" | "error"; rawDir: "ok" | "error" }; company: string | null; /** package.json version */ version: string; /** first 7 chars of the build commit, null when unknown */ build: string | null };

// ---- E9 / E10: users and settings view (ADR-0017) ----

/** GET /api/users (200) `items`; PATCH /api/users/:username (200). Times are ISO strings. */
export type UserItem = {
  username: string; role: Role; active: boolean; locked: boolean; mustSetPassword: boolean;
  createdAt: string; lastLoginAt: string | null;
};
export type UsersResponse = { items: UserItem[] };
/** POST /api/users */
export type CreateUserRequest = { username: string; role: Role };
/** POST /api/users (201) and POST /api/users/:username/password-reset (200). `tempPassword` appears nowhere else (ADR-0017). */
export type CreateUserResponse = { username: string; role: Role; tempPassword: string };
export type PasswordResetResponse = { username: string; tempPassword: string };
/** PATCH /api/users/:username: exactly one of the two. */
export type PatchUserRequest = { role: Role } | { active: boolean };
/** 403 FORBIDDEN extra of a user-command refusal. */
export type UserDeniedExtra = { reason: import("./audit").UserDenyReason };
/** GET /api/settings: the latest settings_versions row. `appliedBy` is "setup" for the first install. */
export type SettingsResponse = {
  version: number; appliedAt: string; appliedBy: string; justification: string; content: import("./settings").Settings;
};
